/**
 * Hook to track page views globally for all routes (public and authenticated)
 * 
 * Automatically tracks page views on route changes using RudderStack analytics.page()
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { useAuth0 } from '@auth0/auth0-react'
import useRudderStackAnalytics from '../useRudderAnalytics'
import { debugLog } from '../utils/log-helper'

/**
 * Check if debug mode is enabled via URL parameter
 */
function isDebugMode(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.get('rs_debug') === '1'
}

/**
 * Hook to track page views on route changes
 * 
 * Should be mounted in BaseLayout to track all routes (public and authenticated)
 */
export function useRudderPageViews(): void {
  const location = useLocation()
  const { isAuthenticated } = useAuth0()
  const [analytics, isReady] = useRudderStackAnalytics()
  const lastKeyRef = useRef<string>('')
  const debug = isDebugMode()

  useEffect(() => {
    // Only track if analytics is ready
    if (!isReady || !analytics) {
      return
    }

    // Create a unique key for this route (pathname + search)
    const routeKey = `${location.pathname}${location.search}`

    // Guard against duplicate firing (same route)
    if (lastKeyRef.current === routeKey) {
      return
    }

    lastKeyRef.current = routeKey

    // Build page view payload
    const pagePayload = {
      path: location.pathname,
      search: location.search,
      url: window.location.href,
      title: document.title,
      logged_in: isAuthenticated,
    }

    // Track page view
    try {
      analytics.page('page_view', pagePayload)

      if (debug) {
        debugLog('[RudderPageViews] Page view tracked:', pagePayload)
      }
    } catch (error) {
      debugLog('[RudderPageViews] Error tracking page view', error)
    }
  }, [location.pathname, location.search, isReady, analytics, isAuthenticated, debug])
}

